import { EyeIcon, PhoneIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@pesto/features';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@pesto/ui';
import { cn } from '@pesto/utils';
import { Cross1Icon } from '@radix-ui/react-icons';

import { useCurrentUserQuery } from '../__generated__/graphql/api';
import { IS_PROD } from '../constants/global-env';
import { useIsPublicRoute } from '../hooks/useIsPublicRoute';
import { useRoleMimickingStore, UserRole } from '../stores/useRoleMimickingStore';

const MIMICKABLE_ROLES = [UserRole.SuperAdmin, UserRole.SupportAgent] as const;

const roleDropdown: Record<(typeof MIMICKABLE_ROLES)[number], { icon: React.ReactNode; label: string }> = {
  [UserRole.SuperAdmin]: {
    icon: <UserIcon className="h-4 w-4" />,
    label: 'Admin',
  },
  [UserRole.SupportAgent]: {
    icon: <PhoneIcon className="h-4 w-4" />,
    label: 'Support Agent',
  },
};

export const RoleMimicSelector = () => {
  const { currentRole, isMimicking, setRole, reset } = useRoleMimickingStore();
  const { isAuthenticated } = useAuth();
  const isPublicRoute = useIsPublicRoute();
  const { data } = useCurrentUserQuery({ skip: isPublicRoute || !isAuthenticated() });
  const backendRole = data?.currentUser?.role;

  if (IS_PROD) return null;

  if (backendRole !== UserRole.SuperAdmin) return null;

  return (
    <DropdownMenu>
      <div className="flex items-center">
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'bg-background ml-auto h-8 w-auto p-2',
              isMimicking && 'border-primary bg-primary/10 rounded-r-none border',
            )}
            onClick={() => {}}
          >
            <EyeIcon className="h-4 w-4" /> View as
          </Button>
        </DropdownMenuTrigger>
        {isMimicking && (
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'bg-background ml-auto h-8 w-auto p-2',
              isMimicking && 'border-primary bg-primary/10 rounded-l-none border border-l-0',
            )}
            onClick={() => reset()}
          >
            <Cross1Icon className="h-4 w-4" />
          </Button>
        )}
      </div>
      <DropdownMenuContent align="end">
        {MIMICKABLE_ROLES.map((role) => (
          <DropdownMenuItem
            className={cn('flex items-center gap-2', currentRole === role && 'bg-primary text-primary-foreground')}
            key={role}
            onClick={() => setRole(role)}
          >
            {roleDropdown[role].icon} {roleDropdown[role].label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
